import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { Button } from '../ui/button.js';
import { Label } from '../ui/label.js';
import { Progress } from '../ui/progress.js';
import { useAuth } from '@clerk/clerk-react';
import { Upload, X, Video } from 'lucide-react';
import { useState, useRef, useEffect, useCallback } from 'react';
import { toast } from '../../lib/toast.js';

/**
 * Beautiful drag-and-drop video upload component
 * Uploads to /api/v1/media endpoint and returns CDN URL
 */
function VideoUploadDropzone({ value, onChange, label = 'Video', maxSizeMB = 50, required = false, error, }) {
    const { getToken } = useAuth();
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [previewUrl, setPreviewUrl] = useState(value);
    const fileInputRef = useRef(null);
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    /**
     * Sync previewUrl with external value prop changes
     * This ensures the component displays existing videos when editing
     */
    useEffect(() => {
        setPreviewUrl(value);
    }, [value]);
    /**
     * Validate file type and size
     */
    const validateFile = useCallback((file) => {
        const validTypes = ['video/mp4', 'video/quicktime', 'video/webm', 'video/ogg'];
        if (!validTypes.includes(file.type)) {
            return 'Please upload a valid video file (MP4, MOV, WebM, or OGG)';
        }
        if (file.size > maxSizeBytes) {
            return `File size must be less than ${maxSizeMB}MB`;
        }
        return null;
    }, [maxSizeBytes, maxSizeMB]);
    /**
     * Upload file to server
     */
    const uploadFile = useCallback(async (file) => {
        try {
            setIsUploading(true);
            setUploadProgress(0);
            const formData = new FormData();
            formData.append('file', file);
            // Simulate progress (replace with actual progress if API supports it)
            const progressInterval = setInterval(() => {
                setUploadProgress(prev => Math.min(prev + 5, 90));
            }, 300);
            // Use fetch directly instead of apiClient to avoid JSON transformation
            const token = await getToken();
            const headers = {
                Authorization: `Bearer ${token}`,
            };
            const fetchResponse = await fetch('/api/v1/media', {
                method: 'POST',
                body: formData,
                headers,
            });
            if (!fetchResponse.ok) {
                const errorData = await fetchResponse.json();
                throw new Error(errorData.error || 'Upload failed');
            }
            const response = await fetchResponse.json();
            clearInterval(progressInterval);
            setUploadProgress(100);
            // Set the CDN URL
            const data = response.data || response;
            onChange(data.url);
            setPreviewUrl(data.url);
            toast.success('Video uploaded successfully');
        }
        catch (err) {
            const errorMessage = err?.message || 'Failed to upload video';
            toast.error(errorMessage);
        }
        finally {
            setIsUploading(false);
            setUploadProgress(0);
        }
    }, [getToken, onChange]);
    /**
     * Handle file selection
     */
    const handleFileSelect = useCallback((file) => {
        const validationError = validateFile(file);
        if (validationError) {
            toast.error(validationError);
            return;
        }
        uploadFile(file);
    }, [validateFile, uploadFile]);
    /**
     * Handle drag events
     */
    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };
    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };
    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        if (file) {
            handleFileSelect(file);
        }
    };
    /**
     * Handle file input change
     */
    const handleInputChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };
    /**
     * Handle remove video
     */
    const handleRemove = () => {
        onChange(undefined);
        setPreviewUrl(undefined);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };
    /**
     * Open file picker
     */
    const handleClick = () => {
        fileInputRef.current?.click();
    };
    return (jsxs("div", { className: "grid gap-2", children: [label && (jsxs(Label, { children: [label, required && jsx("span", { className: "text-destructive ml-1", children: "*" })] })), jsx("input", { ref: fileInputRef, type: "file", accept: "video/mp4,video/quicktime,video/webm,video/ogg", onChange: handleInputChange, className: "hidden" }), previewUrl && !isUploading ? (jsx("div", { className: "relative group", children: jsxs("div", { className: "relative overflow-hidden rounded-xl border-2 border-border bg-muted h-48 flex items-center justify-center p-4", children: [jsx("video", { src: previewUrl, controls: true, className: "h-full w-auto object-contain rounded-xl max-w-full" }), jsxs("div", { className: "absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2", children: [jsxs(Button, { type: "button", variant: "secondary", size: "sm", onClick: handleClick, children: [jsx(Upload, { className: "h-4 w-4 mr-2" }), "Replace"] }), jsxs(Button, { type: "button", variant: "destructive", size: "sm", onClick: handleRemove, children: [jsx(X, { className: "h-4 w-4 mr-2" }), "Remove"] })] })] }) })) : (jsx("div", { onClick: handleClick, onDragOver: handleDragOver, onDragLeave: handleDragLeave, onDrop: handleDrop, className: `
            relative cursor-pointer rounded-xl border-2 border-dashed transition-all
            ${isDragging ? 'border-primary bg-primary/5' : 'border-border bg-muted/50'}
            ${isUploading ? 'pointer-events-none' : 'hover:border-primary hover:bg-primary/5'}
            h-48 flex flex-col items-center justify-center gap-3 p-6
          `, children: isUploading ? (jsxs(Fragment, { children: [jsx(Upload, { className: "h-12 w-12 text-muted-foreground animate-pulse" }), jsxs("div", { className: "w-full max-w-xs space-y-2", children: [jsx(Progress, { value: uploadProgress, className: "h-2" }), jsxs("p", { className: "text-sm text-muted-foreground text-center", children: ["Uploading... ", uploadProgress, "%"] })] })] })) : (jsxs(Fragment, { children: [jsx("div", { className: "rounded-full bg-primary/10 p-4", children: jsx(Video, { className: "h-8 w-8 text-primary" }) }), jsxs("div", { className: "text-center space-y-1", children: [jsxs("p", { className: "text-sm font-medium", children: [jsx("span", { className: "text-primary", children: "Click to upload" }), " or drag and drop"] }), jsxs("p", { className: "text-xs text-muted-foreground", children: ["MP4, MOV, WebM up to ", maxSizeMB, "MB"] })] })] })) })), error && jsx("p", { className: "text-sm text-destructive", children: error })] }));
}

export { VideoUploadDropzone };
//# sourceMappingURL=VideoUploadDropzone.js.map
